import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Users2, Search, Copy, Mail, Crown, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const PLAN_STYLE = {
  cocon: "bg-amber-400/15 text-amber-300 border-amber-400/30",
  elan: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  free: "bg-white/5 text-foreground/50 border-white/10",
};

function copyToClipboard(text, okMsg) {
  const done = () => toast.success(okMsg);
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => fallback(text, done));
  } else {
    fallback(text, done);
  }
}
function fallback(text, done) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta); done();
  } catch { toast.error("Copie impossible"); }
}

export default function UsersSection() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/users", { params: { limit: 2000 } })
      .then(({ data }) => { setUsers(data.users || []); setTotal(data.total || 0); })
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s) ||
      (u.handle || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  const copyAllEmails = () => {
    const emails = filtered.map((u) => u.email).filter(Boolean).join(", ");
    if (!emails) return toast("Aucune adresse à copier");
    copyToClipboard(emails, `${filtered.length} adresse(s) copiée(s)`);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <div className="space-y-5" data-testid="users-section">
      <div className="glass rounded-2xl border border-primary/20 p-6 frame-pulse">
        <h3 className="font-display text-xl text-gradient-orange flex items-center gap-2 mb-1.5">
          <Users2 size={19} /> Utilisateurs
        </h3>
        <p className="text-foreground/55 text-sm mb-5">
          Liste des adresses e-mail des explorateurs inscrits. {total} compte(s) au total.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un e-mail, un nom, un handle…"
              data-testid="users-search"
              className="w-full bg-black/25 border border-white/10 focus:border-primary/40 rounded-full pl-9 pr-4 py-2.5 text-sm text-foreground/90 outline-none transition-colors"
            />
          </div>
          <button
            onClick={copyAllEmails}
            data-testid="users-copy-all"
            className="inline-flex items-center justify-center gap-2 text-sm text-primary border border-primary/30 hover:bg-primary/10 rounded-full px-4 py-2.5 transition-colors flex-shrink-0"
          >
            <Copy size={15} /> Copier tous les e-mails ({filtered.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-foreground/50 p-8 justify-center">
          <Loader2 className="animate-spin" size={18} /> Chargement…
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl border border-primary/15 overflow-hidden">
          <div className="hidden sm:grid grid-cols-[1.6fr_1fr_auto_auto] gap-4 px-5 py-3 text-[11px] uppercase tracking-wider text-primary/70 border-b border-white/8">
            <span>E-mail</span><span>Nom / Handle</span><span>Forfait</span><span>Inscrit le</span>
          </div>
          <div className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-center text-foreground/45 py-10 text-sm">Aucun utilisateur trouvé.</p>
            )}
            {filtered.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="grid sm:grid-cols-[1.6fr_1fr_auto_auto] gap-2 sm:gap-4 px-5 py-3 items-center hover:bg-white/[0.02] transition-colors">
                <button
                  onClick={() => copyToClipboard(u.email, "Adresse copiée")}
                  className="flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors text-left group"
                  title="Copier cette adresse"
                >
                  <Mail size={13} className="text-primary/50 group-hover:text-primary flex-shrink-0" />
                  <span className="truncate">{u.email}</span>
                  {u.role === "admin" && <ShieldCheck size={13} className="text-amber-300 flex-shrink-0" title="Administrateur" />}
                </button>
                <span className="text-sm text-foreground/60 truncate">
                  {u.name || "—"}{u.handle ? <span className="text-foreground/35"> · @{u.handle}</span> : ""}
                </span>
                <span className={`justify-self-start text-[11px] uppercase tracking-wide px-2.5 py-1 rounded-full border ${PLAN_STYLE[u.plan] || PLAN_STYLE.free} inline-flex items-center gap-1`}>
                  {u.plan === "cocon" && <Crown size={11} />}{u.plan || "free"}
                </span>
                <span className="text-xs text-foreground/50 tabular-nums">{fmtDate(u.created_at)}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
